import { TableTextFilter } from "components/TableTextFilter/TableTextFilter";
import { setScrollBehavior } from "features/scrollPageBehaviorProvider/scrollPageBehaviorProvider";
import { _ } from "i18n";
import type { DiscoverUserRowResponse } from "libs/apiClient";
import client from "libs/client";
import { useEffect, useState } from "react";
import DatagridV2, { type SimpleColumn } from "ui-kit/src/Datagrid/DatagridV2";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import { DiscoverBreadcrumbs } from "features/discover/DiscoverBreadcrumbs";
import { createSortArrows, formatDate, formatDateTime } from "features/discover/functions";

const statusOptions = [{
	value: "true",
	label: "Active"
}, {
	value: "false",
	label: "Inactive"
}];

const isMinorOptions = [{
	label: "Is minor",
	value: "minor"
}, {
	label: "Is Adult",
	value: "adult"
}, {
	label: "Not set",
	value: "notSet"
}];

const entityTypeOptions = [{
	label: "Discover",
	value: "discover"
}, {
	label: "Portal",
	value: "portal"
}, {
	label: "Discover and portal",
	value: "discoverAndPortal"
}, {
	label: "Portal coach",
	value: "coach"
}, {
	label: "Portal club manager",
	value: "clubmanager"
}, {
	label: "Portal scout",
	value: "recruiter"
}, {
	label: "Portal player",
	value: "player"
}, {
	label: "Portal admin",
	value: "admin"
}];

export function DiscoverUsersPage() {
	const [pagination, setPagination] = useState<{ pageIndex: number; pageSize: number }>({
		pageIndex: 0,
		pageSize: 15,
	});
	const defaultEntityType = {
		label: "Discover",
		value: "discover",
	};

    const [filters, setFilters] = useState<{ [key: string]: string | number | undefined }>({entityType: defaultEntityType.value});
    const [filterValues, setFilterValues] = useState<{ [key: string]: string | undefined }>({entityType: defaultEntityType.value});
    const [sort, setSort] = useState<{ field: string, direction: string }>();

	useEffect(() => { setScrollBehavior() }, [])

	const { data, isLoading } = client.getDiscoverUsers.useQuery({
		page: pagination.pageIndex + 1, limit: pagination.pageSize, filters: [
			...(filters?.id ? [{
				field: "id",
				type: "search",
				value: filters.id,
			}] : []),
			...(filters?.clubId ? [{
				field: "clubId",
				type: "exact",
				value: filters.clubId,
			}] : []),
			...(filters?.entityType ? [{
				field: "scope",
				type: "exact",
				value: filters.entityType,
			}] : []),
			...(filters?.firstName ? [{
				field: "firstName",
				type: "search",
				value: filters.firstName,
			}] : []),
			...(filters?.middleName ? [{
				field: "middleName",
				type: "search",
				value: filters.middleName,
			}] : []),
			...(filters?.lastName ? [{
				field: "lastName",
				type: "search",
				value: filters.lastName,
			}] : []),
			...(filters?.email ? [{
				field: "email",
				type: "search",
				value: filters.email,
			}] : []),
			...(filters?.phoneNumber ? [{
				field: "phoneNumber",
				type: "search",
				value: filters.phoneNumber,
			}] : []),
			...(filters?.isActive ? [{
				field: "isActive",
				type: "boolean",
				value: filters.isActive === "true",
			}] : []),
			...(filters?.isMinor ? [{
				field: "isMinor",
				type: "exact",
				value: filters.isMinor,
			}] : []),
		],
		order: sort
	})

	const handleSetFilter = (key: string, value?: number | string,) => {
		setFilters({ ...filters, [key]: value });
		setPagination({ ...pagination, pageIndex: 0 });
	}

	const handleClearFilter = (key: string) => {
		setFilters({ ...filters, [key]: undefined })
	}

	const handleSort = (newSort?: { field: string, direction: string }) => {
		setSort(newSort);
	}

	const generateColumnHeader = (columnName: string, label: string, isNumber?: boolean, className?: string) => () => (
		<div className={`tableHeader ${className}`}>
			<TableTextFilter
				value={filterValues[columnName] || ""}
				onChange={(value) => setFilterValues({ ...filterValues, [columnName]: value })}
				number={isNumber}
				clear={() => {
					handleClearFilter(columnName);
					setFilterValues({ ...filterValues, [columnName]: undefined })
				}}
				onSubmit={(value: string) => handleSetFilter(columnName, value)}
				placeholder={label}
			/>
			{createSortArrows(columnName, sort, handleSort)}
		</div>
	);

	const simpleColumns: SimpleColumn<DiscoverUserRowResponse>[] = [
		{
			id: "id",
			key: "id",
			header: generateColumnHeader("id", "ID", true, "tableHeader--id"),
			cell: (value, row) => row.id ?? "-",
		},
		{
			id: "entityType",
			key: "entityType",
			cell: (value, row) => {
				let parts: string[] = [];

				if (row.hasDiscover) {
					parts.push('Discover')
				}

				if (row.hasPlatform) {
					if (row.entityType === 'admin') {
						parts.push('Admin')
					} else if (row.entityType === 'recruiter') {
						parts.push('Scout')
					} else if (row.entityType === 'clubManager') {
						parts.push('Club manager')
					} else {
						parts.push('Portal ' + row.entityType)
					}
				}

				return <div className="thinCell">{parts.join(', ')}</div>
			},
			header: () => <SelectComponent
				value={entityTypeOptions.find((o) => o.value === filters?.entityType)}
				handleChange={(value) => handleSetFilter("entityType", value?.value)}
				placeholder="Type"
				items={entityTypeOptions}
				isCleareable
			/>
		},
		{
			id: "firstName",
			key: "firstName",
			header: generateColumnHeader("firstName", "First Name"),
			cell: (value, row) => row.firstName ?? "-",
		},
		{
			id: "middleName",
			key: "middleName",
			header: generateColumnHeader("middleName", "Middle Name"),
			cell: (value, row) => row.middleName ?? "-",
		},
		{
			id: "lastName",
			key: "lastName",
			header: generateColumnHeader("lastName", "Last Name"),
			cell: (value, row) => row.lastName ?? "-",
		},
		{
			id: "email",
			key: "email",
			header: generateColumnHeader("email", "email"),
			cell: (value, row) => <div className="thinCell">{row.email ?? "-"}</div>,
		},
		{
			id: "phoneNumber",
			key: "phoneNumber",
			header: generateColumnHeader("phoneNumber", "phone", true),
			cell: (value, row) => <div className="thinCell">{row.phoneNumber ?? "-"}</div>,
		},
		{
			id: "isActive",
			key: "isActive",
			header: () => <SelectComponent
				value={statusOptions.find((o) => o.value === filters?.isActive)}
				handleChange={(value) => handleSetFilter("isActive", value?.value)}
				placeholder="active"
				items={statusOptions}
				isCleareable
			/>,
			cell: (value, row) => <div className="thinCell"><div>{row.isActive ? "Active" : "Inactive"}</div></div>,
		},
		{
			id: "isMinor",
			key: "isMinor",
			header: () => <SelectComponent
				value={isMinorOptions.find((o) => o.value === filters?.isMinor)}
				handleChange={(value) => handleSetFilter("isMinor", value?.value)}
				placeholder="Age"
				items={isMinorOptions}
				isCleareable
			/>,
			cell: (value, row) => <div className="thinCell">{value === 1 ? `Minor ${formatDate(row.birthday)}` : value === 0 ? `Adult ${formatDate(row.birthday)}` : "-"}</div>,
		},
		{
			id: "passwordRequestedAt",
			key: "passwordRequestedAt",
			header: () => <div className="tableHeader">
				<span>Password requested at</span>
				{createSortArrows("passwordRequestedAt", sort, handleSort)}
			</div>,
			cell: (value) => <div className="thinCell">{formatDateTime(value as string | null)}</div>
		},
	]

	return (
		<div className="discover-container discover-container--wide">
			<div className="pageHeading">
				<DiscoverBreadcrumbs items={[
					{
						label: 'Users',
					},
				]} />
			</div>
			<div className="discover-card discover-card--for-table">
				<DatagridV2
					getRowLink={(row) => `/discover/user/${row.id}`}
					isLoading={isLoading}
					simpleColumns={simpleColumns}
					data={data?.data.items ?? []}
					pageCount={data?.data?.pages ?? 1}
					pagination={pagination}
					setPagination={setPagination}
					noResults={_("datatables.zero_records")}
				/>
			</div>
		</div>
	)
}
